import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

const paragraphs = (text) => (text || "").split("\n").map((p) => p.trim()).filter(Boolean);

function BlogPost({ slug }) {
  const { lang } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data.post)).catch(() => setPost(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  if (!post) return (
    <div className="min-h-[50vh] grid place-items-center text-center px-5">
      <div>
        <h1 className="font-serif text-3xl mb-4">{lang === "es" ? "Entrada no encontrada" : "Post not found"}</h1>
        <Link to="/blog" className="link-underline text-sm">{lang === "es" ? "Volver al blog" : "Back to blog"}</Link>
      </div>
    </div>
  );

  return (
    <article data-testid="blog-post" className="mx-auto max-w-3xl px-5 lg:px-10 py-16">
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm link-underline mb-8" data-testid="blog-back-link">
        <ArrowLeft className="h-4 w-4" /> {lang === "es" ? "Volver al blog" : "Back to blog"}
      </Link>
      <p className="overline text-gold mb-3">{post[`category_${lang}`]}</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light leading-tight">{post[`title_${lang}`]}</h1>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-4">
        <CalendarDays className="h-4 w-4" /> {post.date}
      </div>
      {post.image && (
        <div className="relative overflow-hidden aspect-[16/9] bg-secondary my-10">
          <img src={post.image} alt={post[`title_${lang}`]} className="h-full w-full object-cover" />
        </div>
      )}
      <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
        {paragraphs(post[`body_${lang}`]).map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </article>
  );
}

export default function Blog() {
  const { lang } = useLanguage();
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) return;
    setLoading(true);
    api.get("/blog").then((r) => setPosts(r.data.posts)).finally(() => setLoading(false));
  }, [slug]);

  if (slug) return <BlogPost slug={slug} />;

  if (loading) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page">
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 pt-16 pb-10 text-center">
        <p className="overline text-gold mb-4">{lang === "es" ? "Diario" : "Journal"}</p>
        <h1 className="font-serif text-5xl sm:text-6xl font-light">Blog</h1>
        <p className="mt-5 text-muted-foreground max-w-2xl mx-auto">
          {lang === "es"
            ? "Historias, guías y consejos sobre joyería fina, relojes y el arte de coleccionar piezas para toda la vida."
            : "Stories, guides and tips on fine jewelry, watches and the art of collecting pieces for a lifetime."}
        </p>
      </section>

      {posts.length === 0 ? (
        <div className="py-24 text-center text-muted-foreground" data-testid="blog-empty">
          {lang === "es" ? "Pronto publicaremos nuevas entradas." : "New posts coming soon."}
        </div>
      ) : (
        <>
          {featured && (
            <section className="mx-auto max-w-[1400px] px-5 lg:px-10 pb-16">
              <Link to={`/blog/${featured.slug}`} className="group grid lg:grid-cols-2 gap-8 items-center" data-testid={`blog-card-${featured.slug}`}>
                <div className="relative overflow-hidden aspect-[16/10] bg-secondary">
                  <img src={featured.image} alt={featured[`title_${lang}`]} className="hover-zoom-img h-full w-full object-cover" />
                </div>
                <div>
                  <p className="overline text-gold mb-3">{featured[`category_${lang}`]}</p>
                  <h2 className="font-serif text-3xl sm:text-4xl font-light leading-tight group-hover:text-gold transition-colors">{featured[`title_${lang}`]}</h2>
                  <p className="mt-4 text-muted-foreground leading-relaxed">{featured[`excerpt_${lang}`]}</p>
                  <span className="inline-flex items-center gap-2 text-sm mt-6 link-underline">
                    {lang === "es" ? "Leer más" : "Read more"} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </section>
          )}

          <section className="mx-auto max-w-[1400px] px-5 lg:px-10 pb-24">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {rest.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="group flex flex-col" data-testid={`blog-card-${post.slug}`}>
                  <div className="relative overflow-hidden aspect-[4/3] bg-secondary">
                    <img src={post.image} alt={post[`title_${lang}`]} loading="lazy" className="hover-zoom-img h-full w-full object-cover" />
                  </div>
                  <p className="overline text-gold mt-4 mb-2">{post[`category_${lang}`]}</p>
                  <h3 className="font-serif text-2xl font-light leading-snug group-hover:text-gold transition-colors">{post[`title_${lang}`]}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{post[`excerpt_${lang}`]}</p>
                  <span className="inline-flex items-center gap-2 text-sm mt-4 text-foreground/70">{post.date}</span>
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
